/**
 * Iframe Communication Contract v1
 *
 * Minimal one-shot INIT from Angular host → React app.
 * After INIT, Angular does NOT send further commands.
 * React handles everything: API calls, state, saving, permissions, locking.
 *
 * Angular → React:  INIT  (single message after READY)
 * React  → Angular: READY | ERROR | NAVIGATE
 */

// =============================================================================
// COMPONENT / ENTITY TYPES
// =============================================================================

export type ComponentType =
  | 'textEditor'
  | 'spreadsheet'
  | 'gantt'
  | 'workflow'
  | 'runInfo'
  | 'runAnalysis'
  | 'graphViewer';

export type EntityType =
  | 'TEXTFILE_VERSION'
  | 'SPREADSHEETFILE_VERSION'
  | 'GANTT_VERSION'
  | 'VERSION'
  | 'RUN'
  | 'WETLABWORKFLOW'
  | 'RUN_ANALYSIS'
  | 'GRAPH_VIEWER';

// =============================================================================
// ANGULAR → REACT: INIT PAYLOAD
// =============================================================================

export interface InitPayload {
  /** Contract version for forward compatibility */
  version: '1.0';
  /** Which React component to render */
  componentType: ComponentType;
  /** Backend entity type */
  entityType: EntityType;
  /** Backend entity ID (e.g. versionId, runId) */
  entityId: string;
  /** Lifecycle history entry to open instead of the live entity content */
  historyEntryId?: string;
  /** Auth token for React to call APIs directly */
  authToken: string;
  /** Optional: UI hints (theme, locale) */
  uiContext?: {
    theme?: string;
    locale?: string;
  };
}

export interface InitMessage {
  type: 'INIT';
  payload: InitPayload;
  timestamp: number;
}

// =============================================================================
// REACT → ANGULAR: EVENTS
// =============================================================================

export type IframeEventType = 'READY' | 'ERROR' | 'NAVIGATE' | string;

export interface IframeEvent<T = unknown> {
  type: IframeEventType;
  payload?: T;
  timestamp: number;
}

export interface ErrorPayload {
  code: string;
  message: string;
  details?: unknown;
}

export interface NavigatePayload {
  entityType: EntityType;
  entityId: string;
  entityName?: string;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

export function createEvent<T = unknown>(
  type: IframeEventType,
  payload?: T
): IframeEvent<T> {
  return { type, payload, timestamp: Date.now() };
}

export function isInitMessage(msg: unknown): msg is InitMessage {
  return (
    typeof msg === 'object' &&
    msg !== null &&
    (msg as InitMessage).type === 'INIT' &&
    typeof (msg as InitMessage).payload === 'object' &&
    typeof (msg as InitMessage).timestamp === 'number'
  );
}